<template>
  <section id="login">
    <b-field label="Username">
      <b-input
        required
        maxlength="50"
        placeholder="Username"
        v-model="username"
        @keyup.enter="handleEnterKey"
      ></b-input>
    </b-field>

    <b-field label="Password">
      <b-input
        type="password"
        placeholder="Password"
        required
        password-reveal
        v-model="password"
        @keyup.enter="handleEnterKey"
      ></b-input>
    </b-field>

    <div class="login-buttons">
      <b-button
        type="is-info"
        class="login-button"
        :loading="isLoadingStudent"
        :disabled="isLoadingTeacher"
        @click="loginAsStudent"
      >
        <span class="icon">
          <i class="fas fa-user-graduate"></i>
        </span>
        <span>Login as Student</span>
      </b-button>

      <b-button
        type="is-primary"
        class="login-button"
        :loading="isLoadingTeacher"
        :disabled="isLoadingStudent"
        @click="loginAsTeacher"
      >
        <span class="icon">
          <i class="fas fa-chalkboard-teacher"></i>
        </span>
        <span>Login as Coach</span>
      </b-button>
    </div>

    <div class="has-text-centered mt-5 mb-4">
      <span class="has-text-grey-light">OR</span>
    </div>

    <b-button
      type="is-link"
      class="is-fullwidth"
      outlined
      @click="redirectToSAC"
    >
      <span class="icon">
        <i class="fas fa-external-link-alt"></i>
      </span>
      <span>Login via SAC</span>
    </b-button>
  </section>
</template>

<script>
import sacService from "@/services/sac";
import { authenticate } from "@/services/sso";
import { mapActions } from "vuex";

export default {
  name: "Login",

  data() {
    return {
      isLoadingStudent: false,
      isLoadingTeacher: false,
      username: null,
      password: null,
    };
  },

  computed: {
    sacLoginUrl() {
      // Determine SAC login URL based on environment
      // In production, use the production SAC website
      // In development, use the dev SAC website
      const isDev = process.env.VUE_APP_PROJECT_ID?.includes("dev");
      return isDev
        ? "https://secure.dev.sydneyacademyofchess.com.au"
        : "https://secure.sydneyacademyofchess.com.au";
    },
  },

  methods: {
    ...mapActions(["updateTeacherProfile"]),

    handleEnterKey() {
      // Default to student login on Enter key
      if (!this.isLoadingStudent && !this.isLoadingTeacher) {
        this.loginAsStudent();
      }
    },

    redirectToSAC() {
      // Redirect to SAC login page
      window.location.href = this.sacLoginUrl;
    },

    async loginAsStudent() {
      if (!this.username || !this.password) {
        this.$buefy.toast.open({
          message: "Please enter username and password",
          type: "is-warning",
        });
        return;
      }

      this.isLoadingStudent = true;
      try {
        // Clear teacher profile and token before student login
        this.$store.commit("clearTeacherProfile");

        const response = await authenticate(this.username, this.password);
        if (!response || !response.access_token) {
          this.$buefy.toast.open({
            message: "Invalid username or password!",
            type: "is-danger",
          });
          return;
        }

        localStorage.setItem("accessToken", response.access_token);

        // Check if there's a redirect query parameter
        const redirectTo = this.$route.query.redirect;
        if (redirectTo && redirectTo !== "/login") {
          this.$router.replace(redirectTo);
        } else {
          this.$router.replace({ name: "home" });
        }
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          message: "Login failed. Please try again.",
          type: "is-danger",
        });
      } finally {
        this.isLoadingStudent = false;
      }
    },

    async loginAsTeacher() {
      if (!this.username || !this.password) {
        this.$buefy.toast.open({
          message: "Please enter username and password",
          type: "is-warning",
        });
        return;
      }

      this.isLoadingTeacher = true;
      try {
        // Clear student profile and token before teacher login
        this.$store.commit("clearProfile");

        await sacService.login(this.username, this.password);

        // Save teacher profile to store after successful login
        const teacherInfo = sacService.teacherInfo();
        if (teacherInfo.teacherId) {
          this.updateTeacherProfile(teacherInfo);
        }

        this.$router.push({ name: "TeacherHome" });
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          message: "Login failed. Please check your credentials.",
          type: "is-danger",
        });
      } finally {
        this.isLoadingTeacher = false;
      }
    },
  },

  async mounted() {
    // Check if already authenticated as teacher
    const isTeacherAuth = sacService.isAuthenticated();
    if (isTeacherAuth) {
      // Save teacher profile to store if already authenticated
      const teacherInfo = sacService.teacherInfo();
      if (teacherInfo.teacherId) {
        this.updateTeacherProfile(teacherInfo);
      }
      this.$router.replace({ name: "TeacherHome" });
      return;
    }

    // Student authentication is checked by the router guard
  },
};
</script>

<style scoped>
#login {
  background-color: white;
  width: 380px;
  min-height: 320px;
  padding: 2rem;
  margin-top: 5rem;
  margin-bottom: 10rem;
  position: relative;
  margin-left: auto;
  margin-right: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.login-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.login-button {
  width: 100%;
  justify-content: center;
}
</style>
